/**
 * Feature flag configuration for the admin dashboard.
 * ---------------------------------------------------------------------------
 * This is the single place to turn admin pages/features on or off. Each flag
 * governs one or more routes (matched by exact path or path prefix) and has a
 * status:
 *
 *   - 'enabled'      → page renders normally.
 *   - 'coming-soon'  → route stays reachable but shows a designed "Coming soon"
 *                      page instead of the real content. Nav links remain
 *                      visible so users can discover what's on the way.
 *   - 'disabled'     → page is hidden from the sidebar AND blocked if visited
 *                      directly (renders an "unavailable" notice).
 *
 * To change what's live, edit the `status` of an entry below — nothing else in
 * the app needs to change. A route with no matching flag defaults to 'enabled'.
 *
 * Matching: a flag governs `path` when `path` equals one of its `routes` or
 * starts with `route + "/"`. When several flags match, the one with the longest
 * matching route wins — so a specific sub-route can override a section flag.
 */

export type FeatureStatus = 'enabled' | 'coming-soon' | 'disabled';

export interface FeatureFlag {
  /** Stable identifier for the flag. */
  key: string;
  /** Human-readable label (used in "unavailable" / coming-soon copy). */
  label: string;
  /** Current status. */
  status: FeatureStatus;
  /** Route paths (exact or prefix) this flag controls. */
  routes: string[];
  /** Optional copy shown on the coming-soon page for this feature. */
  comingSoon?: {
    title?: string;
    description?: string;
    /** Free-form ETA, e.g. "Q3 2026" or "Coming this summer". */
    eta?: string;
  };
}

/**
 * The flag registry. Add or edit entries here to control the admin.
 * Everything defaults to 'enabled'; you only need entries for routes you want
 * to turn into coming-soon or disable.
 */
export const featureFlags: FeatureFlag[] = [
  // ---- Examples: flip `status` to control these sections ------------------
  {
    key: 'escrow',
    label: 'Escrow',
    status: 'coming-soon',
    routes: ['/admin/escrow'],
    comingSoon: {
      title: 'Escrow is on the way',
      description:
        'The full escrow suite — transactions, disputes, compliance and financial controls — is being built. Check back soon.',
      eta: 'Coming soon',
    },
  },
  {
    key: 'brain-agent',
    label: 'Brain',
    status: 'enabled',
    routes: ['/admin/ai'],
  },
  {
    key: 'partners',
    label: 'Partners',
    status: 'disabled',
    routes: ['/admin/escrow/providers'],
  },
  {
    key: 'listing-bookings',
    label: 'Reservations & Bookings',
    status: 'disabled',
    routes: ['/admin/listings/bookings'],
    comingSoon: {
      title: 'Bookings admin is on the way',
      description:
        'Reservations and bookings management has no admin backend yet. It will light up here once it ships.',
      eta: 'Coming soon',
    },
  },

  // ---- Everything below is live. Kept here as a ready-to-edit inventory. ---
  { key: 'overview', label: 'Overview', status: 'enabled', routes: ['/admin'] },
  { key: 'human-resource', label: 'People', status: 'enabled', routes: ['/admin/human-resource'] },
  { key: 'accounting', label: 'Accounting', status: 'enabled', routes: ['/admin/accounting'] },
  { key: 'analytics', label: 'Analytics', status: 'enabled', routes: ['/admin/analytics'] },
  { key: 'support', label: 'Support', status: 'enabled', routes: ['/admin/support'] },
  { key: 'users', label: 'Users', status: 'enabled', routes: ['/admin/users'] },
  { key: 'organizations', label: 'Organizations', status: 'enabled', routes: ['/admin/organizations'] },
  { key: 'badges', label: 'Badges', status: 'enabled', routes: ['/admin/badges'] },
  { key: 'listings', label: 'Listings', status: 'enabled', routes: ['/admin/listings'] },
  { key: 'assets', label: 'Assets', status: 'enabled', routes: ['/admin/assets'] },
  { key: 'service-requests', label: 'Service Requests', status: 'disabled', routes: ['/admin/service-requests'] },
  { key: 'promotions', label: 'Promotions', status: 'enabled', routes: ['/admin/promotions', '/admin/highlights'] },
  { key: 'vouchers', label: 'Vouchers', status: 'enabled', routes: ['/admin/vouchers'] },
  { key: 'payments', label: 'Payments', status: 'enabled', routes: ['/admin/payments'] },
  { key: 'communications', label: 'Communications', status: 'enabled', routes: ['/admin/communications'] },
  {
    key: 'notification-delivery',
    label: 'Notification delivery',
    status: 'enabled',
    routes: [
      '/admin/communications/notifications/delivery',
      '/admin/communications/notifications/routing',
      '/admin/communications/notifications/reconciliation',
      '/admin/communications/notifications/providers',
    ],
  },
  { key: 'campaigns', label: 'Campaigns & Relationships', status: 'enabled', routes: ['/admin/relationship'] },
  { key: 'navigation', label: 'Navigation', status: 'enabled', routes: ['/admin/navigation'] },
  { key: 'apps-catalog', label: 'Apps Catalog', status: 'enabled', routes: ['/admin/apps-catalog'] },
  { key: 'content', label: 'Content', status: 'enabled', routes: ['/admin/content'] },
  { key: 'marketing', label: 'Marketing', status: 'enabled', routes: ['/admin/marketing'] },
  { key: 'sales', label: 'Sales', status: 'enabled', routes: ['/admin/sales'] },
  { key: 'media', label: 'Radar & Sources (under Marketing)', status: 'enabled', routes: ['/admin/marketing/radar', '/admin/marketing/sources'] },
  { key: 'feature-flags', label: 'Features', status: 'enabled', routes: ['/admin/features', '/admin/feature-flags'] },
  { key: 'settings', label: 'Settings', status: 'enabled', routes: ['/admin/settings'] },
];

// ---------------------------------------------------------------------------
// Matching helpers
// ---------------------------------------------------------------------------

/** Strip query string, hash and trailing slash so paths compare cleanly. */
export function normalizePath(pathOrUrl: string): string {
  const path = pathOrUrl.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) return path.slice(0, -1);
  return path;
}

/** True when `route` governs `path` (exact match or prefix segment match). */
function routeMatches(route: string, path: string): boolean {
  const r = normalizePath(route);
  return path === r || path.startsWith(r + '/');
}

/**
 * Find the flag governing a given path. When multiple flags match, the one with
 * the longest matching route wins (most specific), so a sub-route flag can
 * override its section.
 */
export function getFeatureFlagForPath(pathOrUrl: string): FeatureFlag | undefined {
  const path = normalizePath(pathOrUrl);
  let best: FeatureFlag | undefined;
  let bestLen = -1;

  for (const flag of featureFlags) {
    for (const route of flag.routes) {
      const r = normalizePath(route);
      if (routeMatches(r, path) && r.length > bestLen) {
        best = flag;
        bestLen = r.length;
      }
    }
  }
  return best;
}

/** Resolve the effective status for a path. Unmatched paths are 'enabled'. */
export function resolvePathStatus(pathOrUrl: string): FeatureStatus {
  return getFeatureFlagForPath(pathOrUrl)?.status ?? 'enabled';
}

/** A route is accessible in nav/direct-visit unless it is disabled. */
export function isPathAccessible(pathOrUrl: string): boolean {
  return resolvePathStatus(pathOrUrl) !== 'disabled';
}

// ---------------------------------------------------------------------------
// Navigation filtering
// ---------------------------------------------------------------------------

// Minimal shape needed to filter nav; mirrors NavigationItem without importing
// it here to keep this config free of UI-type coupling.
interface NavLike {
  url?: string;
  items?: NavLike[];
}

/**
 * Remove disabled routes from a navigation tree. Group headers (url === '#')
 * are dropped when all their children are removed. Coming-soon routes are kept
 * so users can still discover them.
 */
export function filterNavigation<T extends NavLike>(items: T[]): T[] {
  const result: T[] = [];

  for (const item of items) {
    const hasChildren = Array.isArray(item.items) && item.items.length > 0;
    const isGroupHeader = !item.url || item.url === '#';
    const children = hasChildren
      ? filterNavigation(item.items as T[])
      : undefined;

    if (isGroupHeader) {
      // Pure group: keep only if at least one child survived.
      if (children && children.length > 0) {
        result.push({ ...item, items: children });
      }
      continue;
    }

    // Leaf or navigable group: drop if its own route is disabled.
    if (!isPathAccessible(item.url as string)) continue;

    if (hasChildren) {
      result.push({
        ...item,
        items: children && children.length ? children : undefined,
      });
    } else {
      result.push(item);
    }
  }

  return result;
}
